/**
 * Geofence scope-priority tip component (HANDOFF_2026-05-25 Bug 19).
 *
 * Owner directive: 'department，group，woker 的交集，谁大于谁，要给
 * 用户做 tips'. The 3 geofence scope pages (Department / Group / Worker)
 * pre-fix had inconsistent messaging — Department's sub-tagline
 * mentioned worker override, Group's sub-tagline mentioned both, and
 * Worker had a broken bottom tip ('Workers without overrides follow
 * their department geofence') that was wrong on the page that IS the
 * override list.
 *
 * This component renders a uniform info Alert at the top of every
 * scope page so admins see the same precedence story everywhere:
 *
 *   Worker override > Group geofence > Department geofence > Org default
 *
 * Highlight the current page's scope so users understand where in the
 * hierarchy they're editing.
 */
import React from 'react';
import { Alert, Box, Chip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

type GeofenceScope = 'worker' | 'group' | 'department';

interface GeofenceScopePriorityTipProps {
  current: GeofenceScope;
}

const GeofenceScopePriorityTip: React.FC<GeofenceScopePriorityTipProps> = ({
  current,
}) => {
  const { t } = useTranslation();
  const SCOPE_ORDER: { key: GeofenceScope; label: string }[] = [
    { key: 'worker', label: t('attendance:geofences.scopeWorker') },
    { key: 'group', label: t('attendance:geofences.scopeGroup') },
    { key: 'department', label: t('attendance:geofences.scopeDepartment') },
  ];

  return (
    <Alert
      severity="info"
      icon={<InfoOutlinedIcon fontSize="small" />}
      variant="outlined"
      sx={{
        width: '100%',
        mb: 2,
        py: 0.75,
        '& .MuiAlert-message': { width: '100%' },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography
          variant="body2"
          sx={{ fontSize: '0.825rem', fontWeight: 600, color: 'text.primary' }}
        >
          {t('attendance:geofences.priorityTipTitle')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.75,
            mt: 0.25,
          }}
        >
          {SCOPE_ORDER.map((s, i) => (
            <React.Fragment key={s.key}>
              <Chip
                label={s.label}
                size="small"
                sx={{
                  borderRadius: 1,
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: s.key === current ? 700 : 500,
                  bgcolor: s.key === current ? 'primary.main' : 'grey.200',
                  color: s.key === current ? 'primary.contrastText' : 'text.secondary',
                }}
              />
              {i < SCOPE_ORDER.length - 1 && (
                <Typography
                  component="span"
                  sx={{ fontSize: '0.8rem', color: 'text.disabled' }}
                >
                  →
                </Typography>
              )}
            </React.Fragment>
          ))}
        </Box>
        <Typography
          variant="caption"
          sx={{ fontSize: '0.72rem', color: 'text.secondary', mt: 0.25 }}
        >
          {t('attendance:geofences.priorityTipFooter')}
        </Typography>
      </Box>
    </Alert>
  );
};

export default GeofenceScopePriorityTip;
export { GeofenceScopePriorityTip };
